import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { investigationRows } from "./investigations";
import { getAccessToken, lockApp } from "./access";

const KEY = "criticalc_session";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const emptyCase = () => ({
  step: 0,
  maxStep: 0,
  model: "gemini",
  patient: { name: "", age: "", sex: "", blood_group: "", rh: "" },
  investigations: {},
  history: {},
  followup: { questions: [], answers: {} },
  tentative: null,
  vitals: {},
  labs: {},
  exam: { general: {}, systems: {} },
  refined: null,
  selectedInv: [],
  extraAnswers: {},
  results: {},
  otherResults: "",
  final: null,
});

const Ctx = createContext(null);

export function SessionProvider({ children }) {
  const [state, setState] = useState(() => {
    try { return { ...emptyCase(), ...JSON.parse(sessionStorage.getItem(KEY) || "{}") }; } catch { return emptyCase(); }
  });
  useEffect(() => { sessionStorage.setItem(KEY, JSON.stringify(state)); }, [state]);

  const set = useCallback((path, value) => {
    setState((s) => {
      const keys = path.split(".");
      const next = { ...s };
      let cur = next;
      keys.slice(0, -1).forEach((k) => { cur[k] = { ...cur[k] }; cur = cur[k]; });
      cur[keys[keys.length - 1]] = value;
      if (path === "step") next.maxStep = Math.max(s.maxStep || 0, value);
      return next;
    });
  }, []);
  const patch = useCallback((obj) => setState((s) => ({ ...s, ...obj })), []);
  const reset = useCallback(() => { sessionStorage.removeItem(KEY); setState(emptyCase()); }, []);

  const [pending, setPending] = useState({});
  const inflight = useRef({});
  const run = useCallback(async (task, caseData, apply) => {
    if (inflight.current[task]) return;
    inflight.current[task] = true;
    setPending((p) => ({ ...p, [task]: true }));
    try { apply(await callAI(task, state.model, caseData)); }
    catch (e) { toast.error(aiError(e)); }
    inflight.current[task] = false;
    setPending((p) => ({ ...p, [task]: false }));
  }, [state.model]);

  return <Ctx.Provider value={{ state, set, patch, reset, run, pending }}>{children}</Ctx.Provider>;
}

export const useSession = () => useContext(Ctx);

export const aiError = (e) => e?.response?.data?.detail && typeof e.response.data.detail === "string"
  ? e.response.data.detail : "AI request failed. Please retry.";

const guarded = async (url, opts = {}) => {
  const res = await fetch(url, { ...opts, headers: { ...(opts.headers || {}), "X-Access-Token": getAccessToken() } });
  if (res.status === 401) lockApp();
  return res;
};

export async function callAI(task, model, caseData) {
  const post = await guarded(`${API}/ai/${task}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ model, case: caseData }),
  });
  const start = await post.json().catch(() => ({}));
  if (!post.ok) throw { response: { data: start } }; // eslint-disable-line no-throw-literal
  for (;;) {
    await new Promise((r) => setTimeout(r, 1000));
    const res = await guarded(`${API}/ai/job/${start.job_id}`);
    const job = await res.json().catch(() => ({}));
    if (!res.ok) throw { response: { data: job } }; // eslint-disable-line no-throw-literal
    if (job.status === "done") return job.result;
    if (job.status === "error") throw { response: { data: { detail: job.error } } }; // eslint-disable-line no-throw-literal
    window.dispatchEvent(new CustomEvent("cc-progress", { detail: job.n }));
  }
}

const brief = (corr) => corr?.map(({ item, type, points_to, gap }) => ({ item, type, points_to, gap }));

export function buildCase(s, scores, upto) {
  const c = { patient: s.patient, history: s.history };
  if (upto === "followup") return c;
  c.followup_answers = s.followup.questions.map((q) => ({ question: q.question, answer: s.followup.answers[q.id] || "not answered" }));
  if (upto === "tentative") return { ...c, prior_textbook_correlation: brief(s.followup.correlation) };
  c.tentative_ddx = s.tentative?.ddx?.map((d) => d.diagnosis);
  c.vitals = s.vitals;
  c.labs_for_scores = s.labs;
  c.computed_scores = scores;
  c.examination = s.exam;
  if (upto === "refine") return { ...c, prior_textbook_correlation: brief(s.tentative?.textbook_correlation) };
  c.refined_ddx = s.refined?.ddx?.map((d) => d.diagnosis);
  c.additional_answers = (s.refined?.additional_questions || []).filter((q) => s.extraAnswers?.[q.id])
    .map((q) => ({ asked_to: q.ask, question: q.question, answer: s.extraAnswers[q.id] }));
  c.investigation_panel = investigationRows(s.investigations).map(([g, lb, v]) => `${g} - ${lb}: ${v}`);
  c.investigation_reports = Object.fromEntries(Object.entries(s.results).filter(([, v]) => v));
  c.other_reports = s.otherResults;
  c.prior_textbook_correlation = brief(s.refined?.textbook_correlation);
  return c;
}
